<script setup lang="ts">
import { computed } from 'vue';

type Opacity = 'low' | 'mid' | 'high';
type Size = '2xs' | 'sm' | 'md' | 'lg' | 'xl' | '2xl' | '3xl' | '4xl' | '5xl' | '6xl';

interface Props {
  icon: string;
  color?: string;
  size?: Size;
  opacity?: Opacity;
  selected?: boolean;
}

const { icon, color = '', size = 'md', opacity = 'high', selected = false } = defineProps<Props>();

const isFlag = computed<boolean>(() => icon.includes('flag'));
const flagCode = computed<string>(() => isFlag.value && icon.replaceAll('flag-', '').toLowerCase());

const sizes: Record<Size, string> = {
  '2xs': '0.625rem',
  xs: '0.75rem',
  sm: '0.875rem',
  md: '1rem',
  lg: '1.125rem',
  xl: '1.25rem',
  '2xl': '1.5rem',
  '3xl': '1.875rem',
  '4xl': '2.25rem',
  '5xl': '3rem',
  '6xl': '8rem',
};

const opacities: Record<Opacity, number> = {
  low: 0.3,
  mid: 0.6,
  high: 1,
};

const fontSize = computed<Size>(() => sizes[size]);

const styles = computed<Record<string, string>>(() => {
  const commonStyles = { opacity: opacities[opacity] };

  return isFlag.value
    ? { ...commonStyles, width: sizes[size], height: sizes[size], 'min-width': sizes[size] }
    : { ...commonStyles, color };
});
</script>

<template>
  <svg
    v-if="isFlag"
    class="flag"
    :style="{
      ...styles,
      'clip-path': 'var(--flag-radius)',
    }"
    shape-rendering="geometricPrecision">
    <use :href="`#${flagCode}`" />
  </svg>

  <i
    v-else
    :class="[
      'icon n-i',
      icon,
      {
        'icon--selected': selected,
      },
    ]"
    :style="styles"
    aria-hidden="true" />
</template>

<style scoped lang="scss">
@use '@nsftx/nfont/css/_icons.scss' with (
  $font-icon-name: 'NFont',
  $font-icon-path: 'https://assets.nsoft-cdn.com/public/gravity/icons/nfont'
);

.icon {
  font-size: v-bind(fontSize);
  color: inherit;

  &--selected {
    color: var(--primary);
  }
}
</style>
